/**
 * Ticket di origine di un report ENISA: il ticket (pagina /tickets) la cui promozione ha creato o
 * alimentato il report.
 *
 * Fonte primaria: `payload.originTicketIds`, registrato da srp-service a ogni promozione (copre
 * anche più ticket confluiti nella stessa bozza di release). Fallback per i report promossi prima
 * del tracciamento: i ticket promossi che puntano al report (`ticket.srpReportId`).
 */

import { Link } from 'react-router-dom';
import { useTickets } from '../triage/hooks.js';
import { ticketPath } from '../triage/routes.js';
import type { Ticket } from '../triage/types.js';
import type { SrpReport } from './types.js';
import styles from './Srp.module.css';

/** Id dei ticket di origine del report, senza duplicati, nell'ordine di promozione. */
export function ticketDiOrigine(
  report: Pick<SrpReport, 'id' | 'payload'>,
  promossi: readonly Pick<Ticket, 'id' | 'srpReportId'>[] = [],
): number[] {
  const dalPayload = Array.isArray(report.payload.originTicketIds)
    ? (report.payload.originTicketIds as unknown[]).filter(
        (x): x is number => typeof x === 'number' && Number.isInteger(x) && x > 0,
      )
    : [];
  const daiTicket = promossi.filter((t) => t.srpReportId === report.id).map((t) => t.id);
  return [...new Set([...dalPayload, ...daiTicket])];
}

/**
 * Resolver dei ticket di origine per la pagina corrente. I ticket promossi (fallback) sono letti
 * una volta per tutta la lista; se la lettura fallisce resta la fonte primaria.
 */
export function useTicketDiOrigine(): (report: Pick<SrpReport, 'id' | 'payload'>) => number[] {
  const { data: promossi } = useTickets('promoted');
  // Risposta inattesa (non una lista): si ignora il fallback, il payload resta la fonte.
  const lista = Array.isArray(promossi) ? promossi : [];
  return (report) => ticketDiOrigine(report, lista);
}

/** Link ai ticket di origine ("Ticket #12"), o un trattino se il report non ne ha. */
export function LinkTicketOrigine({
  reportId,
  ticketIds,
}: {
  reportId: number;
  ticketIds: number[];
}) {
  if (ticketIds.length === 0) {
    return (
      <span className={styles.muted ?? ''} data-testid={`ticket-origine-${reportId}`}>
        —
      </span>
    );
  }
  return (
    <span className={styles.ticketOrigine} data-testid={`ticket-origine-${reportId}`}>
      {ticketIds.map((id) => (
        <Link
          key={id}
          to={ticketPath(id)}
          onClick={(e) => e.stopPropagation()}
          title="Apri il ticket chiuso che ha originato il report"
          data-testid={`link-ticket-origine-${reportId}-${id}`}
        >
          Ticket #{id}
        </Link>
      ))}
    </span>
  );
}
